import axios from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import useLocationInfoById from 'pared/components/LocationInfo/hooks/useLocationInfo'
import { FE2_BASE_URL } from 'pared/constants'
import { getBrandLocationGroupId } from 'pared/utils/brand'
import { getUser } from 'pared/utils/user'

import { useGroupFilter } from '../groupFilter'
import useLocationInfoByGroupFilter from '../title/hooks/useLocationInfo'
import { UseFeV2ChatBoxProps, UseFeV2ChatBoxReturn } from './types'
import {
  convertBrandCodeForDb,
  getAgentCodeFromPath,
  getCustomerCodeFromPath,
} from './utils'

const PAGE_DATA_MESSAGE_TYPE = 'PAGE_DATA_UPDATE'

export const useFeV2ChatBox = ({
  agentType,
  description,
  bubblePrompts,
  disableInputField,
  sendStoreId,
  useThinking,
  useLocalAgent,
}: UseFeV2ChatBoxProps): UseFeV2ChatBoxReturn => {
  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [iframeUrl, setIframeUrl] = useState('')
  const [iframeReady, setIframeReady] = useState(false)
  const [tokenRefreshCount, setTokenRefreshCount] = useState(0)
  const [systemPrompt, setSystemPrompt] = useState<string | undefined>(
    undefined,
  )
  const [systemPromptVisibleOnly, setSystemPromptVisibleOnly] = useState<
    string | undefined
  >(undefined)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const location = useLocation()

  const userInfo = getUser()

  const { data: locationDisplayNameByFilter } = useLocationInfoByGroupFilter()
  const { groupFilter } = useGroupFilter()

  const store = useMemo(() => {
    if (groupFilter?.ids?.[0]) {
      return groupFilter.ids[0].toString()
    }
    const queryObject = new URLSearchParams(location.search)
    return queryObject.get('store') || userInfo.defaultLocationId?.toString()
  }, [groupFilter, location.search, userInfo])

  const locationInfoById = useLocationInfoById(store ? parseInt(store, 10) : 0)

  const locationDisplayName = useMemo(() => {
    if (locationDisplayNameByFilter) {
      return locationDisplayNameByFilter.replace('Store: ', '')
    }
    if (locationInfoById) {
      return locationInfoById.displayName
    }
    return null
  }, [locationDisplayNameByFilter, locationInfoById])

  const agentCode = useMemo(
    () => getAgentCodeFromPath(location.pathname),
    [location.pathname],
  )

  const brandCode = useMemo(
    () => getCustomerCodeFromPath(location.pathname),
    [location.pathname],
  )

  const customerCode = useMemo(
    () => convertBrandCodeForDb(brandCode),
    [brandCode],
  )

  const processedBubblePrompts = useMemo(() => {
    if (!sendStoreId || !bubblePrompts) {
      return bubblePrompts
    }

    return bubblePrompts.map((bubble) => ({
      ...bubble,
      prompt: locationDisplayName
        ? `${bubble.prompt} for ${locationDisplayName}`
        : bubble.prompt,
    }))
  }, [sendStoreId, bubblePrompts, locationDisplayName])

  const fe2BaseUrl = FE2_BASE_URL
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const embedConfig = useMemo(() => {
    const { email } = getUser()
    const brandLocationGroupId = getBrandLocationGroupId()
    const storeId = sendStoreId && store ? parseInt(store, 10) : undefined

    return {
      customerCode,
      configKey: agentCode,
      agentType,
      description,
      bubblePrompts: processedBubblePrompts,
      isInIframe: true,
      timezone,
      enabled: true,
      isExpoUser: false,
      useThinking: useThinking || false,
      useLocalAgent: useLocalAgent || false,
      disableInputField: disableInputField || false,
      email,
      storeId: storeId && !isNaN(storeId) ? storeId : undefined,
      brandLocationGroupId,
    }
  }, [
    customerCode,
    agentCode,
    agentType,
    description,
    processedBubblePrompts,
    timezone,
    useThinking,
    useLocalAgent,
    disableInputField,
    sendStoreId,
    store,
  ])

  useEffect(() => {
    const fetchEmbedUrl = async () => {
      if (!customerCode || !agentCode) return

      try {
        const res = await axios.post(
          `${fe2BaseUrl}/api/souschef/embed`,
          embedConfig,
        )

        const token = res.data.embedToken
        setIframeUrl(`${fe2BaseUrl}/iframe/chatbox?token=${token}`)
      } catch (err) {
        console.error('Failed to create embed token:', err)
        const params = new URLSearchParams({
          customerCode,
          configKey: agentCode,
          timezone,
        })
        if (agentType) {
          params.append('agentType', agentType)
        }
        if (useThinking) {
          params.append('useThinking', 'true')
        }
        setIframeUrl(`${fe2BaseUrl}/iframe/chatbox?${params.toString()}`)
      }
    }

    fetchEmbedUrl()
  }, [
    embedConfig,
    fe2BaseUrl,
    customerCode,
    agentCode,
    timezone,
    agentType,
    useThinking,
    tokenRefreshCount,
  ])

  useEffect(() => {
    setIframeReady(false)

    const handleIframeLoad = () => {
      setTimeout(() => {
        setIframeReady(true)
      }, 500)
    }

    const handleMessage = (event: MessageEvent) => {
      if (iframeUrl) {
        const iframeOrigin = new URL(iframeUrl).origin
        if (event.origin === iframeOrigin) {
          if (event.data?.type === 'IFRAME_READY') {
            setIframeReady(true)
          } else if (event.data?.type === 'TOKEN_INVALID') {
            setTokenRefreshCount((c) => c + 1)
          }
        }
      }

      if (
        event.data?.type === PAGE_DATA_MESSAGE_TYPE &&
        event.data?.systemPrompt
      ) {
        setSystemPrompt(event.data.systemPrompt)
        setSystemPromptVisibleOnly(event.data.systemPromptVisibleOnly)
      }
    }

    const currentIframe = iframeRef.current

    window.addEventListener('message', handleMessage)
    if (currentIframe) {
      currentIframe.addEventListener('load', handleIframeLoad)
    }

    return () => {
      window.removeEventListener('message', handleMessage)
      if (currentIframe) {
        currentIframe.removeEventListener('load', handleIframeLoad)
      }
    }
  }, [iframeUrl])

  useEffect(() => {
    if (!sendStoreId || !iframeReady || !store || !iframeUrl) {
      return
    }

    const storeIdNum = parseInt(store, 10)
    if (!storeIdNum || isNaN(storeIdNum)) {
      return
    }

    const sendMessageToIframe = (
      storeId: number,
      brandLocationGroupId: number,
    ) => {
      if (iframeRef.current?.contentWindow) {
        const iframeOrigin = new URL(iframeUrl).origin
        iframeRef.current.contentWindow.postMessage(
          { storeId, brandLocationGroupId },
          iframeOrigin,
        )
      }
    }

    const brandLocationGroupId = getBrandLocationGroupId()
    sendMessageToIframe(storeIdNum, brandLocationGroupId)
  }, [sendStoreId, iframeReady, store, iframeUrl])

  useEffect(() => {
    if (!systemPrompt || !iframeReady || !iframeUrl) {
      return
    }

    if (iframeRef.current?.contentWindow) {
      const iframeOrigin = new URL(iframeUrl).origin
      iframeRef.current.contentWindow.postMessage(
        {
          type: PAGE_DATA_MESSAGE_TYPE,
          systemPrompt,
          systemPromptVisibleOnly,
        },
        iframeOrigin,
      )
    }
  }, [systemPrompt, systemPromptVisibleOnly, iframeReady, iframeUrl])

  return {
    isOpen,
    setIsOpen,
    isExpanded,
    setIsExpanded,
    iframeUrl,
    iframeRef,
    userInfo,
  }
}
